import React from 'react';
import { motion } from 'framer-motion';
import { Check, AlertCircle, ArrowDown, BookOpen, Battery, Zap, Droplets, Clock, XCircle, ChevronRight } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
const scrollToOffer = () => {
  const element = document.getElementById('oferta');
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }
};
type CardProps = {
  children: React.ReactNode;
  variant?: 'default' | 'pain' | 'solution' | 'offer' | 'transition' | 'policy';
  className?: string;
  id?: string;
};
const Card = ({
  children,
  variant = 'default',
  className = '',
  id
}: CardProps) => {
  const baseStyles = 'w-full p-5 rounded-xl border mb-3 relative overflow-hidden';
  const variants = {
    default: 'bg-[#141414] border-white/5',
    pain: 'bg-[#1a1010] border-red-900/20',
    solution: 'bg-[#101a15] border-emerald-900/20',
    offer: 'bg-[#1a1500] border-amber-500/30 shadow-[0_0_30px_-10px_rgba(245,158,11,0.15)]',
    transition: 'bg-transparent border-none py-8 text-center',
    policy: 'bg-[#0f0f0f] border-white/5 text-xs text-gray-500'
  };
  return <motion.div initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-50px'
  }} variants={fadeInUp} className={`${baseStyles} ${variants[variant]} ${className}`} id={id} data-id="element-1">
      {children}
    </motion.div>;
};
const CTAButton = ({
  text = 'ACCEDER',
  fullWidth = false,
  size = 'normal',
  variant = 'primary',
  href
}: {
  text?: string;
  fullWidth?: boolean;
  size?: 'normal' | 'large' | 'small';
  variant?: 'primary' | 'secondary';
  href?: string;
}) => {
  const baseClass = 'font-bold rounded-lg transition-all duration-200 active:scale-95 flex items-center justify-center gap-2 cursor-pointer';
  const sizes = {
    small: 'py-2 px-4 text-sm',
    normal: 'py-3 px-6 text-base',
    large: 'py-4 px-8 text-xl shadow-lg shadow-amber-500/20'
  };
  const variants = {
    primary: 'bg-amber-500 hover:bg-amber-400 text-black',
    secondary: 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
  };
  const className = `${baseClass} ${sizes[size]} ${variants[variant]} ${fullWidth ? 'w-full' : ''}`;
  if (href) {
    return <a href={href} className={className} data-id="element-2">
        {text} <ChevronRight className="w-4 h-4" data-id="element-3" />
      </a>;
  }
  return <button onClick={scrollToOffer} className={className} data-id="element-4">
      {text} <ChevronRight className="w-4 h-4" data-id="element-5" />
    </button>;
};
export function SalesPage() {
  return <div className="min-h-screen bg-[#0a0a0a] text-gray-300 font-sans selection:bg-amber-500/30" data-id="element-6">
      {/* HERO IMAGE */}
      <div className="w-full" data-id="element-7">
        <img src="https://cdn.magicpatterns.com/uploads/mym21pd8xn3rrg23RUnLPP/Supera_el_cansancio_foto3.png" alt="Mujer con energía entrenando" className="w-full h-auto object-cover" data-id="element-8" />
      </div>

      {/* SECOND IMAGE */}
      <div className="w-full" data-id="element-9"></div>

      <div className="max-w-md mx-auto px-4 pb-12 pt-4 relative z-10" data-id="element-10">
        {/* CARD 1 — HERO */}
        <Card variant="default" className="pt-6 border-amber-500/20 shadow-2xl shadow-black" data-id="element-11">
          <div className="inline-block px-3 py-1 rounded-full bg-amber-500/10 text-amber-500 text-xs font-bold mb-3 border border-amber-500/20" data-id="element-12">
            NUEVO MÉTODO
          </div>
          <h1 className="text-3xl font-bold text-white leading-tight mb-2" data-id="element-13">
            Superar el Cansancio Extremo después de Entrenar
          </h1>
          <h2 className="text-lg text-amber-500 font-medium mb-4" data-id="element-14">
            Identifica tu causa en 7 días con el PROTOCOLO 3F (Fuel–Fluids–Flow)
          </h2>
          <p className="mb-6 text-gray-400 leading-snug" data-id="element-15">
            Deja de salir del entreno pensando "¿qué me pasa?". Usa un sistema
            simple para convertir tus síntomas en un plan de acción
            (antes/durante/después) y recuperar estabilidad.
          </p>
          <div className="flex items-center justify-between mb-4 bg-white/5 p-3 rounded-lg" data-id="element-16">
            <span className="text-sm text-gray-400" data-id="element-17">
              Precio habitual:{' '}
              <span className="line-through decoration-red-500" data-id="element-18">$19</span>
            </span>
            <span className="text-xl font-bold text-white" data-id="element-19">Hoy $6.39</span>
          </div>
          <CTAButton fullWidth size="large" data-id="element-20" />
        </Card>

        {/* CARD 2 — ANTES */}
        <Card variant="pain" data-id="element-21">
          <div className="flex items-center gap-2 mb-3 text-red-400" data-id="element-22">
            <XCircle className="w-5 h-5" data-id="element-23" />
            <h3 className="font-bold text-lg" data-id="element-24">
              Antes: entrenas y el día se te rompe
            </h3>
          </div>
          <p className="leading-snug" data-id="element-25">
            Terminas peor. Y luego viene lo que nadie ve: te cuesta trabajar,
            estudiar, hacer recados o simplemente estar de buen humor. No es
            "cansancio". Es quedarte apagada.
          </p>
        </Card>

        {/* CARD 3 — SÍNTOMAS */}
        <Card variant="pain" data-id="element-26">
          <h3 className="font-bold text-lg text-white mb-4" data-id="element-27">
            Si te pasa esto, esto es para ti
          </h3>
          <ul className="space-y-3 mb-6" data-id="element-28">
            {['Cansancio extremo que no cuadra', 'Mareo al terminar', 'Debilidad "me quedo sin fuerza"', 'Dolor de cabeza post-entreno', 'Náuseas', 'Sensación de vacío', 'Cero energía tras ejercicio leve', 'Bajón horas después'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm" data-id="element-29">
                <div className="mt-1 min-w-[6px] h-[6px] rounded-full bg-red-500/50" data-id="element-30" />
                <span data-id="element-31">{item}</span>
              </li>)}
          </ul>
          <CTAButton text="ACCEDER" size="small" fullWidth variant="secondary" data-id="element-32" />
        </Card>

        {/* CARD 4 — BUCLE MENTAL */}
        <Card variant="pain" data-id="element-33">
          <h3 className="font-bold text-lg text-white mb-3" data-id="element-34">
            La parte más frustrante: no saber qué tocar
          </h3>
          <p className="leading-snug" data-id="element-35">
            Un día pruebas comer más. Otro día comes menos. Otro día bebes agua.
            Otro día descansas. Y mientras tanto, tu confianza se va al suelo:
            "igual mi cuerpo no vale para esto".
          </p>
        </Card>

        {/* CARD 5 — EL ENEMIGO */}
        <Card variant="pain" data-id="element-36">
          <h3 className="font-bold text-lg text-white mb-3" data-id="element-37">
            Google te da frases que no te sirven
          </h3>
          <p className="leading-snug mb-4 italic text-gray-400 border-l-2 border-red-500/30 pl-3" data-id="element-38">
            "Hidrátate", "come antes", "descansa", "es normal", "se está
            adaptando".
          </p>
          <p className="leading-snug" data-id="element-39">
            ¿Resultado? Sigues igual porque nadie te dice: cuánto, cuándo, qué
            va primero y cómo medir si era eso.
          </p>
        </Card>

        {/* CARD 6 — DESBLOQUEO */}
        <Card variant="transition" data-id="element-40">
          <h3 className="text-2xl font-bold text-white mb-3" data-id="element-41">
            No necesitas más información. Necesitas orden.
          </h3>
          <p className="text-gray-400" data-id="element-42">
            La mayoría no falla por falta de voluntad. Falla por falta de un
            método. Cuando tienes un método, dejas de improvisar.
          </p>
        </Card>

        {/* CARD 7 — MECANISMO */}
        <Card variant="solution" data-id="element-43">
          <div className="flex items-center gap-2 mb-4 text-emerald-400" data-id="element-44">
            <Zap className="w-5 h-5" data-id="element-45" />
            <h3 className="font-bold text-lg" data-id="element-46">
              PROTOCOLO 3F (lo que cambia el juego)
            </h3>
          </div>
          <div className="space-y-4 mb-6" data-id="element-47">
            <div className="flex gap-3" data-id="element-48">
              <div className="bg-emerald-500/10 p-2 rounded h-fit text-emerald-400" data-id="element-49">
                <Battery className="w-5 h-5" data-id="element-50" />
              </div>
              <div data-id="element-51">
                <strong className="text-white block" data-id="element-52">FUEL</strong>
                <span className="text-sm" data-id="element-53">energía + timing (qué/cuándo)</span>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-54">
              <div className="bg-blue-500/10 p-2 rounded h-fit text-blue-400" data-id="element-55">
                <Droplets className="w-5 h-5" data-id="element-56" />
              </div>
              <div data-id="element-57">
                <strong className="text-white block" data-id="element-58">FLUIDS</strong>
                <span className="text-sm" data-id="element-59">
                  líquidos + criterio práctico para sales
                </span>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-60">
              <div className="bg-amber-500/10 p-2 rounded h-fit text-amber-400" data-id="element-61">
                <Clock className="w-5 h-5" data-id="element-62" />
              </div>
              <div data-id="element-63">
                <strong className="text-white block" data-id="element-64">FLOW</strong>
                <span className="text-sm" data-id="element-65">
                  ritmo, pausas, transiciones, calor
                </span>
              </div>
            </div>
          </div>
          <div className="bg-black/30 p-3 rounded text-xs text-center border border-white/5" data-id="element-66">
            Siempre igual: síntoma → palanca → acción exacta → medición
            (48–72h).
          </div>
        </Card>

        {/* CARD 8 — DESPUÉS */}
        <Card variant="solution" data-id="element-67">
          <h3 className="font-bold text-lg text-white mb-3" data-id="element-68">
            Después: sales con más estabilidad (y con control)
          </h3>
          <p className="leading-snug" data-id="element-69">
            No es "ser perfecta". Es saber qué hacer cuando aparece tu patrón.
            El objetivo es que tu post-entreno deje de ser una lotería.
          </p>
        </Card>

        {/* CARD 9 — QUÉ CAMBIA HOY */}
        <Card variant="solution" data-id="element-70">
          <h3 className="font-bold text-lg text-white mb-3" data-id="element-71">
            Hoy mismo dejas de adivinar
          </h3>
          <p className="leading-snug mb-4" data-id="element-72">
            En el PDF tienes un mapa: eliges tu síntoma dominante, aplicas 1
            ajuste durante 48–72h y mides. Solo con eso ya sales del caos.
          </p>
          <CTAButton text="ACCEDER" size="small" fullWidth variant="secondary" data-id="element-73" />
        </Card>

        {/* CARD 10 — LO QUE HAY DENTRO */}
        <Card variant="solution" data-id="element-74">
          <div className="flex items-center gap-2 mb-4 text-white" data-id="element-75">
            <BookOpen className="w-5 h-5 text-amber-500" data-id="element-76" />
            <h3 className="font-bold text-lg" data-id="element-77">Dentro del PDF (sin relleno)</h3>
          </div>
          <ul className="space-y-3" data-id="element-78">
            {['Clasificador 60s: normal vs excesivo vs parar', 'Mapa: síntoma → causa probable → acción exacta', 'Protocolos antes/durante/después para cada síntoma', 'Plan 7 días para identificar tu causa sin mezclar variables'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm" data-id="element-79">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" data-id="element-80" />
                <span data-id="element-81">{item}</span>
              </li>)}
          </ul>
        </Card>

        {/* CARD 11 — SÍNTOMAS -> ACCIONES */}
        <Card variant="solution" data-id="element-82">
          <h3 className="font-bold text-lg text-white mb-3" data-id="element-83">
            Esto no es teoría: son decisiones
          </h3>
          <p className="leading-snug mb-3" data-id="element-84">Para cada síntoma te dice:</p>
          <div className="bg-white/5 p-3 rounded mb-3 text-center font-medium text-white" data-id="element-85">
            Qué hacer hoy · qué evitar · cómo saber si funcionó
          </div>
          <p className="leading-snug" data-id="element-86">
            Porque lo que te falta no es "un consejo". Es una secuencia.
          </p>
        </Card>

        {/* CARD 12 — FUEL */}
        <Card variant="default" data-id="element-87">
          <h3 className="font-bold text-lg text-amber-500 mb-3" data-id="element-88">
            "Entreno temprano y no me entra comida"
          </h3>
          <p className="leading-snug mb-4" data-id="element-89">
            Tienes opciones pre/post por tolerancia (incluye alternativas
            ligeras y líquidas) + timing simple para no marearte ni quedarte
            vacía.
          </p>
          <CTAButton text="ACCEDER" size="small" fullWidth variant="secondary" data-id="element-90" />
        </Card>

        {/* CARD 13 — FLUIDS */}
        <Card variant="default" data-id="element-91">
          <h3 className="font-bold text-lg text-blue-400 mb-3" data-id="element-92">
            "Bebo agua y aún así me da el bajón"
          </h3>
          <p className="leading-snug" data-id="element-93">
            Protocolo de cuánto/cuándo + criterios prácticos para entender
            cuándo el problema puede ser "algo más que agua" (especialmente con
            calor/sudor). Sin complicarte.
          </p>
        </Card>

        {/* CARD 14 — FLOW */}
        <Card variant="default" data-id="element-94">
          <h3 className="font-bold text-lg text-emerald-400 mb-3" data-id="element-95">
            El "entreno suave" que te destroza suele ser FLOW
          </h3>
          <p className="leading-snug" data-id="element-96">
            Calentar mal, empezar fuerte, pocas pausas o parar en seco. Ajustes
            mínimos que cambian el post-entreno sin que tengas que vivir
            reventada para que "cuente".
          </p>
        </Card>

        {/* CARD 15 — ESCENARIOS REALES */}
        <Card variant="default" data-id="element-97">
          <h3 className="font-bold text-lg text-white mb-3" data-id="element-98">
            Ajustes por situación real
          </h3>
          <div className="flex flex-wrap gap-2 mb-4" data-id="element-99">
            {['Caminar', 'Gym', 'Cardio suave', 'Retomar', 'Entrenar temprano', 'Calor'].map(tag => <span key={tag} className="bg-white/10 px-2 py-1 rounded text-xs text-gray-300 border border-white/5" data-id="element-100">
                {tag}
              </span>)}
          </div>
          <p className="leading-snug" data-id="element-101">
            En cada uno: 1) qué tocar primero 2) cómo medir si era eso.
          </p>
        </Card>

        {/* CARD 16 — ERRORES */}
        <Card variant="default" data-id="element-102">
          <h3 className="font-bold text-lg text-white mb-3" data-id="element-103">
            10 errores que te dejan KO (y el reemplazo exacto)
          </h3>
          <p className="leading-snug" data-id="element-104">
            Cosas pequeñas con efecto grande: parar en seco, post-entreno en
            blanco, subir volumen al retomar, solo agua con mucho sudor… con su
            reemplazo claro.
          </p>
        </Card>

        {/* CARD 17 — PLAN 7 DÍAS */}
        <Card variant="default" data-id="element-105">
          <h3 className="font-bold text-lg text-white mb-4" data-id="element-106">
            Plan 7 días (para que no vuelvas al caos)
          </h3>
          <div className="space-y-3 mb-6 relative pl-4 border-l border-white/10" data-id="element-107">
            <div className="relative" data-id="element-108">
              <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-emerald-500" data-id="element-109"></span>
              <strong className="text-white" data-id="element-110">Días 1–2:</strong> FLOW
            </div>
            <div className="relative" data-id="element-111">
              <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-emerald-500" data-id="element-112"></span>
              <strong className="text-white" data-id="element-113">Días 3–4:</strong> + FUEL
            </div>
            <div className="relative" data-id="element-114">
              <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-emerald-500" data-id="element-115"></span>
              <strong className="text-white" data-id="element-116">Días 5–6:</strong> + FLUIDS
            </div>
            <div className="relative" data-id="element-117">
              <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]" data-id="element-118"></span>
              <strong className="text-white" data-id="element-119">Día 7:</strong> tu Protocolo
              Personal 3F (para repetir cuando cambies horarios o llegue el
              calor).
            </div>
          </div>
          <CTAButton text="ACCEDER" size="small" fullWidth variant="secondary" data-id="element-120" />
        </Card>

        {/* CARD 18 — FORMATO */}
        <Card variant="default" className="text-center py-6" data-id="element-121">
          <div className="inline-flex items-center justify-center p-3 bg-white/5 rounded-full mb-3" data-id="element-122">
            <ArrowDown className="w-6 h-6 text-amber-500" data-id="element-123" />
          </div>
          <h3 className="font-bold text-lg text-white mb-2" data-id="element-124">
            Acceso inmediato
          </h3>
          <p className="leading-snug text-sm" data-id="element-125">
            PDF descargable. Lo abres en móvil o PC y empiezas hoy.
          </p>
        </Card>

        {/* CARD 19 — OFERTA (TARGET) */}
        <Card variant="offer" id="oferta" className="text-center py-8 border-2 border-amber-500/50" data-id="element-126">
          <h3 className="text-sm font-bold text-amber-500 tracking-widest uppercase mb-2" data-id="element-127">
            Oferta Actual
          </h3>
          <div className="flex items-center justify-center gap-3 mb-4" data-id="element-128">
            <span className="text-xl text-gray-500 line-through decoration-red-500" data-id="element-129">
              $19
            </span>
            <span className="text-5xl font-bold text-white tracking-tight" data-id="element-130">
              $6.39
            </span>
          </div>
          <p className="text-sm text-gray-400 mb-6 max-w-xs mx-auto" data-id="element-131">
            Si esto te está rompiendo entrenos y días, esta es la forma más
            rápida de convertir síntomas en un plan.
          </p>
          <CTAButton fullWidth size="large" href="https://cuidarte360.com/cart/55953880809798:1" data-id="element-132" />
        </Card>

        {/* CARD 20 — POLÍTICA */}
        <Card variant="policy" data-id="element-133">
          <div className="flex items-center gap-2 mb-2 text-gray-400" data-id="element-134">
            <AlertCircle className="w-4 h-4" data-id="element-135" />
            <strong className="text-xs uppercase tracking-wide" data-id="element-136">
              Importante antes de acceder
            </strong>
          </div>
          <p className="leading-relaxed opacity-70" data-id="element-137">
            Producto digital descargable (PDF). Por su naturaleza digital, no se
            realizan reembolsos una vez entregado. No hay garantías ni promesas
            médicas: es una guía práctica para ordenar síntomas y probar ajustes
            de forma responsable.
          </p>
        </Card>

        {/* CARD 21 — CIERRE */}
        <Card variant="transition" className="pb-12" data-id="element-138">
          <h3 className="text-2xl font-bold text-white mb-4" data-id="element-139">
            Si entrenas para estar mejor, no deberías terminar peor
          </h3>
          <p className="text-gray-300 mb-8 text-lg" data-id="element-140">
            No eres floja. No estás "inventando". Solo estás sin sistema.
            <br data-id="element-141" />
            <br data-id="element-142" />
            Accede al PDF y aplica el PROTOCOLO 3F para recuperar control y
            estabilidad.
          </p>
          <CTAButton fullWidth size="large" href="https://cuidarte360.com/cart/55953880809798:1" data-id="element-143" />
        </Card>

        <footer className="text-center text-xs text-gray-600 pb-8 pt-4" data-id="element-144">
          © 2024 Protocolo 3F. Todos los derechos reservados.
        </footer>
      </div>
    </div>;
}